import { useEffect } from 'react';
import { Button, Input, Stack, Textarea } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import useDialogCleanup from '@/lib/hooks/use-dialog-cleanup';
import { Curriculum } from '@/lib/types';
import useCurriculumsStore from '@/utils/use-curriculums';

const curDetailsSchema = z.object({
  name: z.string().min(1, 'Curriculum name is required'),
  // Free text, same as on curCreate — all three are optional.
  description: z.string(),
  learning_outcomes: z.string(),
  prerequisites: z.string(),
});

type CurDetailsValues = z.infer<typeof curDetailsSchema>;

interface CurDetailsModalProps {
  isOpen: boolean;
  onClose(): void;
  curriculum: Curriculum;
}

// Edits the curriculum-wide details first collected on curCreate: name,
// description, learning outcomes, prerequisites. Without this the latter two
// would be write-once-at-creation, since no other screen authors them.
//
// Already-scheduled day plans are deliberately untouched by a save here — they
// snapshotted their copy at copy time (see draftFromSlot) and stay as built;
// only plans added from the curriculum afterwards pick up the new text.
export default function CurDetailsModal({ isOpen, onClose, curriculum }: CurDetailsModalProps) {
  // Always mounted by curBuild (never `{show && <CurDetailsModal/>}`, which would
  // skip Ark UI's close transition and leave the body pointer/scroll lock on),
  // so this is only a StrictMode backstop — see use-dialog-cleanup.
  useDialogCleanup(isOpen);
  const update = useCurriculumsStore((s) => s.update);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CurDetailsValues>({
    resolver: zodResolver(curDetailsSchema),
  });

  // Reset on open rather than via defaultValues: the dialog stays mounted across
  // opens, so a re-open must re-seed from the (possibly refetched) curriculum.
  useEffect(() => {
    if (!isOpen) return;
    reset({
      name: curriculum.name,
      description: curriculum.description,
      learning_outcomes: curriculum.learning_outcomes,
      prerequisites: curriculum.prerequisites,
    });
  }, [isOpen, curriculum, reset]);

  const onSubmit = async (values: CurDetailsValues) => {
    try {
      // PUT /curriculums/:id requires name, so every save sends all four fields
      // rather than a diff. The response carries plans/plan_count/built_count,
      // so the store's wholesale item replacement keeps slot data intact.
      await update(curriculum.id, values);
      toaster.create({ type: 'success', title: 'Curriculum details saved' });
      onClose();
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: error.response?.data?.error ?? 'Failed to save curriculum details',
      });
    }
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Curriculum details</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <Stack as='form' id='curDetails' gap={4} onSubmit={handleSubmit(onSubmit)}>
            <Field label='Curriculum name' invalid={!!errors.name} errorText={errors.name?.message}>
              <Input {...register('name')} placeholder='e.g. U12 Fall Fundamentals' />
            </Field>

            <Field label='Description'>
              <Textarea
                {...register('description')}
                rows={3}
                placeholder='What this curriculum covers (optional)'
              />
            </Field>

            <Field
              label='Learning outcomes'
              helperText='What athletes will be able to do after completing the whole curriculum.'
            >
              <Textarea {...register('learning_outcomes')} rows={3} />
            </Field>

            <Field
              label='Prerequisites'
              helperText='What athletes should already know before starting the curriculum.'
            >
              <Textarea {...register('prerequisites')} rows={2} />
            </Field>
          </Stack>
        </DialogBody>

        <DialogFooter>
          <Button variant='ghost' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button colorPalette='brand' form='curDetails' type='submit' loading={isSubmitting}>
            Save details
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
